import React, { useCallback, useEffect, useContext, useState } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { messages } from 'gw-capability-policychange-common-react';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import wizardConfig from '../../config/bop-wizard-config.json5';
import metadata from './PolicyChangesPage.metadata.json5';
import styles from '../../BOPPolicyChange.module.scss';
import './PolicyChangesPage.messages';
import PortalUtil from '../../../../utils/PortalUtil';
import { valueTypePropDatatypeMap } from 'gw-components-platform-react/ScheduleItemsComponent/ScheduleItemsUtil';
import {ModalNextProvider } from '@jutro/components';
import { messages as commonMessages } from 'gw-platform-translations';

function PolicyChangesPage(props) {
    const { steps } = wizardConfig;
    const { authHeader } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const {
        wizardData, currentStepIndex, changeNextSteps, updateWizardData, policyStatus, createUWActivity, updateCreateUWActivity, jumpToStep
    } = props;
    const { submissionVM, selectedData } = wizardData;
    const {dateUtil} = PortalUtil;
    const { initialValidation, isComponentValid, registerComponentValidation } = useValidation(
        'PolicyChangesPage'
    );
    const [isNoPriorPolicy, setIsNoPriorPolicy] = useState();
    const [isScheduledPolicy, setIsScheduledPolicy] = useState();
    const [minEffectiveDate, setMinEffectiveDate] = useState(false);
    const intl = useContext(IntlContext);

    useEffect(()=>{
        if(!minEffectiveDate){
            const minimumEffectiveDate = _.get(submissionVM, 'baseData.minimumEffectiveDate.value')
            setMinEffectiveDate(minimumEffectiveDate);
            /*
            * TMI calculation for min date is the Policy Effective date or Today's date minus 30, whichever greater - so if the Policy Effective date
            * is  Jan  1st, 2021, then the broker can only choose the date from April 5th, 2021 (Today's date minus 30)
            *
            *  const minDate = new Date();
            *  const newMinEffectiveDate = dateUtil.addDaysToLocalDate(dateUtil.toLocalDate(minDate), - 15)
            *  _.set(submissionVM, 'baseData.minimumEffectiveDate.value', newMinEffectiveDate); 
            */
            updateWizardData(wizardData);
        }        
    }, [submissionVM, setMinEffectiveDate, updateWizardData, wizardData])

    const handleChange = useCallback(
        (options) => {
            _.forEach(selectedData, (value, key) => {
                _.set(wizardData, `selectedData.${key}`, options.includes(key));
            });
            updateWizardData(wizardData);
        },
        [updateWizardData, wizardData, selectedData]
    );

    const onNext = useCallback(async () => {
        const stepMapping = {
            address: {
                "id": "endorsement.farm.directives.templates.farm-valuables.Mailing Address",
                "defaultMessage": "Mailing Address"
            },
            itemDetails: {
                "id": "endorsement.farm.directives.templates.farm-valuables.ItemDetails",
                "defaultMessage": "Item Details"
            },
            namedInsured: {
                "id": "endorsement.farm.directives.templates.farm-valuables.Named Insured or Additional Named Insured(s)",
                "defaultMessage": "Named Insured or Additional Named Insured(s)"
            },
            packages: {
                "id": "endorsement.farm.directives.templates.farm-valuables.Policy change",
                "defaultMessage": "Policy change"
            },
        }
        const description = Object.keys(selectedData).filter((k) => {return selectedData[k]}).map((m) => { return 'Amend ' + translator(stepMapping[m])}).join(', ');

        const response = await EndorsementService.checkEffectiveDateIsValid(
            [submissionVM.value.policyNumber, submissionVM.value.baseData.effectiveDate],
            authHeader
        );
        if (response) {
            submissionVM.value = await EndorsementService.loadEndorsementWithEffectiveDate(
                [submissionVM.value.policyNumber, submissionVM.value.baseData.effectiveDate],
                authHeader
            );            
            submissionVM.description.value = description;
            submissionVM.policyChangeReason_EY.value = "other";
            submissionVM.value = await EndorsementService.saveBOPEndorsement(
                [submissionVM.value],
                authHeader
            );
        }
        
        const newSteps = steps.slice(currentStepIndex + 1, steps.length);
        if (!selectedData.address && !selectedData.namedInsured) {
            newSteps.splice(
                _.findIndex(newSteps, ({ path }) => path === '/policy-details'),
                1
            );
        }
        if (!selectedData.itemDetails && !selectedData.packages) {
            newSteps.splice(
                _.findIndex(newSteps, ({ path }) => path === '/upload-details'),
                1
            );
        }
        changeNextSteps(newSteps);
        return wizardData;
    }, [
        EndorsementService,
        authHeader,
        changeNextSteps,
        selectedData,
        steps,
        submissionVM.value,
        wizardData,
        currentStepIndex
    ]);

    const getCheckBoxValue = useCallback(() => {
        const result = [];
        _.forEach(selectedData, (value, key) => {
            if (value) {
                result.push(key);
            }
        });
        return result;
    }, [selectedData]);

    const handleEffectiveDateChange = (value) => {
        _.set(submissionVM,'baseData.effectiveDate.value',value)
        const endDate = dateUtil.toDate(_.get(submissionVM, 'baseData.periodEndDate.value'));
        const startDate = dateUtil.toDate(_.get(submissionVM,'baseData.effectiveDate.value'));
        const renewalDateObj = _.get(submissionVM, 'baseData.renewalPeriodEndDate.value')
        if(renewalDateObj){
            const renewalEndDate = dateUtil.toDate();
            if(startDate > endDate && startDate < renewalEndDate) {
                ModalNextProvider.showAlert({
                    title: 'Review Effective Date Changes',
                    message: 'Please confirm the policy changes are for Renewal policy',
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).then(() => {
                        updateWizardData(wizardData);
                    }, _.noop);
            } else {
                updateWizardData(wizardData);
            }
        } else {
            updateWizardData(wizardData);
        }
    };

    const startDate = dateUtil.toDate(_.get(submissionVM,'baseData.effectiveDate.value'));
    const minDate = dateUtil.toDate(_.get(submissionVM,'baseData.minimumEffectiveDate.value'));
    const today = new Date().setHours(0, 0, 0, 0);
    var endDate = _.get(submissionVM, 'baseData.maximumEffectiveDate.value');
    var renewalEndDate = _.get(submissionVM, 'baseData.renewalPeriodEndDate.value');
    const maxDate = dateUtil.toDate(renewalEndDate ? renewalEndDate : endDate);

    const getEffectiveDateErrorMessage = () => {
        if(minDate > startDate){
            return [translator(messages.minimumDateErrorMsg, {
                minDate: intl.formatDate(minDate, { year: 'numeric', month: 'short', day: 'numeric' })})]
        } 
        else if(startDate > maxDate) {
            return [translator(messages.maxDateErrorMsg, {
                maxDate: intl.formatDate(maxDate, { year: 'numeric', month: 'short', day: 'numeric' })})]
        }
        return [];               
    }

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        farmPolicyChangeEfectiveDateId: {
            //minDate: _.get(submissionVM, 'baseData.minimumEffectiveDate.value'),
            //maxDate: renewalEndDate ? renewalEndDate : endDate,
            showErrors: false,
            //validationMessages: getEffectiveDateErrorMessage(),
            onValueChange: handleEffectiveDateChange
        },
        typeSelector: {
            value: getCheckBoxValue(),
            onValueChange: handleChange
        },
        backDatedEndorsementSection:{
            visible: today > startDate,
        },
        noPriorClaims:{
            required: true,
            showRequired: true,
            value: isNoPriorPolicy,            
            onValueChange: (value) => {
                setIsNoPriorPolicy(value);
            }
        },
        /*scheduledEndorsementSection:{
            visible: policyStatus == 'Scheduled'            
        },*/
        scheduledPolicyAccept:{
            required: true,
            showRequired: true,
            value: createUWActivity,            
            onValueChange: (value) => {
                updateCreateUWActivity(value);
            }
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const checkSelectedData = useCallback(() => {
        if (
            selectedData.address === false
            && selectedData.itemDetails === false
            && selectedData.namedInsured === false
            && selectedData.packages === false
            
        ) {
            return false;
        }
        return true;
    }, [selectedData]);

    useEffect(() => {
        if(submissionVM.value.status === 'Quoted' && submissionVM.value.description === 'Renewal Policy Change'){
            submissionVM.value.description = '';
            updateWizardData(wizardData);
            navigateToCoverages();
        } else {
            registerComponentValidation(checkSelectedData);
        }
    }, [checkSelectedData, registerComponentValidation]);

    const navigateToCoverages = useCallback(() => {
        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === '/quotes'
        );
        jumpToStep(indexOfGeneralCoverages);
    }, [jumpToStep, steps]);

    return (
        <WizardPage
            onNext={onNext}
            disableNext={!isComponentValid || (today > startDate && !isNoPriorPolicy) }
            skipWhen={initialValidation}
            showPrevious={false}
            cancelLabel={translator(appConfig.persona === 'policyholder' ? messages.cancelAllChanges : messages.cancel)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                onModelChange={updateWizardData}
            />
        </WizardPage>
    );
}

PolicyChangesPage.propTypes = wizardProps;

export default PolicyChangesPage;
